import {computed, ComputedRef, inject, InjectionKey, provide, Ref, shallowRef, unref, watch} from 'vue'
import {RouteLocationNormalizedLoaded, routerViewLocationKey, useRoute} from 'vue-router'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {isStackableRoute} from '@toolify/client/src/setup/router/hooks/isStackableOverlayNavigation'
import {
  getWorkspaceAccessByName,
  IWorkspaceAccess,
} from '@toolify/client/src/modules/workspace/functions/getWorkspaceAccessByName'
import {PermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionType'
import {
  IWorkspaceResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'

export interface ILayerContext {
  route: ComputedRef<RouteLocationNormalizedLoaded>
  workspace: ComputedRef<IWorkspaceResponseInstance | null>
  hasWorkspacePermission(permissionType: PermissionType): boolean
}

const layerContextKey: InjectionKey<ILayerContext> = Symbol('LayerContext')

/**
 * Where a page is, kept per layer: the page under the overlays and every stackable layer over it (an item card)
 * get their own route and their own workspace. A card opened from another workspace's notification or link reads
 * that workspace, while the page under it, its sidebar included, stays on workspaceStore.currentWorkspace.
 */
export function provideLayerContext(routeRef: Ref<RouteLocationNormalizedLoaded | null>): ILayerContext {
  const workspaceStore = useWorkspaceStore()
  const route = computed<RouteLocationNormalizedLoaded>(() => unref(routeRef))
  const ownWorkspaceAccess = shallowRef<IWorkspaceAccess | null>(null)

  // Only a stackable layer can belong to another workspace: ensureWorkspaceRouterHook keeps currentWorkspace on
  // the page, so the page itself always matches it.
  const ownWorkspaceName = computed<string | null>(() => {
    const workspaceName = route.value?.params.workspaceName as string | undefined
    if(!workspaceName || !isStackableRoute(route.value) || workspaceName === workspaceStore.currentWorkspace?.name) {
      return null
    }
    return workspaceName
  })

  watch(ownWorkspaceName, async workspaceName => {
    ownWorkspaceAccess.value = null
    if(!workspaceName) {
      return
    }
    try {
      const workspaceAccess = await getWorkspaceAccessByName(workspaceName)
      if(ownWorkspaceName.value === workspaceName) {
        ownWorkspaceAccess.value = workspaceAccess
      }
    } catch {
      // A card shared without workspace access still opens; it just has no workspace to read.
    }
  }, {
    immediate: true,
  })

  const context: ILayerContext = {
    route,
    workspace: computed(() => {
      if(!ownWorkspaceName.value) {
        return workspaceStore.currentWorkspace
      }
      return ownWorkspaceAccess.value?.workspace ?? null
    }),
    hasWorkspacePermission(permissionType: PermissionType): boolean {
      if(!ownWorkspaceName.value) {
        return workspaceStore.hasPermission(permissionType)
      }
      return Boolean(ownWorkspaceAccess.value?.permissions.some(permission => {
        return permission.type === permissionType && permission.value === true
      }))
    },
  }
  provide(layerContextKey, context)
  return context
}

export function useLayerContext(): ILayerContext {
  const providedContext = inject(layerContextKey, null)
  if(providedContext) {
    return providedContext
  }
  // Outside the router views (app chrome) there is no layer: the global route and workspace. The store is read
  // lazily because useWorkspaceStore itself asks for the route while it is being created.
  const globalRoute = useRoute()
  const routerViewRouteRef = inject<Ref<RouteLocationNormalizedLoaded>>(routerViewLocationKey, null)
  return {
    route: computed<RouteLocationNormalizedLoaded>(() => routerViewRouteRef ? unref(routerViewRouteRef) : globalRoute),
    workspace: computed(() => useWorkspaceStore().currentWorkspace),
    hasWorkspacePermission(permissionType: PermissionType): boolean {
      return useWorkspaceStore().hasPermission(permissionType)
    },
  }
}
